import { useQuery } from '@tanstack/react-query'
import { getCompanyInfo } from '../api/client'
import { LegalShell, type LegalLang } from './legalShared'

// 5651 sayılı Kanun Madde 3/1 gereği tanıtıcı bilgiler (künye). Alanlar admin
// → Platform Ayarları'ndan yönetilir ve public /company-info endpoint'inden
// okunur; boş bırakılan alan sayfada hiç görünmez. Legal sayfalar gibi
// bilingual ve i18next dışıdır (dil URL slug'ından gelir).

const L = {
  tr: {
    title: 'Künye',
    updated: 'Son güncelleme: 4 Eylül 2026',
    intro:
      "Bu sayfa, 5651 sayılı İnternet Ortamında Yapılan Yayınların Düzenlenmesi ve Bu Yayınlar Yoluyla İşlenen Suçlarla Mücadele Edilmesi Hakkında Kanun'un 3. maddesi uyarınca yayınlanan tanıtıcı bilgileri içerir.",
    infoHeading: 'Tanıtıcı Bilgiler',
    legalHeading: 'Hukuki Bildirimler',
    hostingHeading: 'Yer Sağlayıcılık',
    legalIntro:
      'Resmî kurum tebliğleri, içerik kaldırma / erişim engelleme kararları ve diğer hukuki bildirimler için: ',
    hostingStatus:
      'Provisyer, 5651 sayılı Kanun kapsamında yer sağlayıcı sıfatıyla faaliyet gösterir.',
    hostingLiability:
      "Kanun'un 4. maddesi uyarınca, platform üzerinde barındırılan kurulumlardaki içeriklerin sorumluluğu ilgili içerik sağlayıcıya (kurulum sahibi müşteriye) aittir. Yer sağlayıcı, yer sağladığı içeriği kontrol etmek veya hukuka aykırı bir faaliyetin söz konusu olup olmadığını araştırmakla yükümlü değildir (Madde 5/1).",
    labels: {
      legalName: 'Ticaret unvanı',
      tradeName: 'İşletme adı',
      taxOffice: 'Vergi dairesi',
      taxNo: 'Vergi numarası',
      mersisNo: 'MERSİS numarası',
      address: 'Adres',
      phone: 'Telefon',
      email: 'E-posta',
      kep: 'KEP adresi',
      website: 'Web sitesi',
    },
  },
  en: {
    title: 'Legal Notice',
    updated: 'Last updated: September 4, 2026',
    intro:
      'This page contains the identifying information published pursuant to Article 3 of Turkish Law No. 5651 on the Regulation of Publications on the Internet.',
    infoHeading: 'Company Information',
    legalHeading: 'Legal Notifications',
    hostingHeading: 'Hosting Provider Status',
    legalIntro:
      'For official notifications, content removal / access blocking orders and other legal correspondence: ',
    hostingStatus:
      'Provisyer operates as a hosting provider (yer sağlayıcı) within the scope of Law No. 5651.',
    hostingLiability:
      'Pursuant to Article 4 of the Law, responsibility for content hosted on instances on this platform lies with the relevant content provider (the customer owning the instance). The hosting provider is not obliged to monitor hosted content or investigate whether it involves unlawful activity (Article 5/1).',
    labels: {
      legalName: 'Trade name',
      tradeName: 'Business name',
      taxOffice: 'Tax office',
      taxNo: 'Tax number',
      mersisNo: 'MERSIS number',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      kep: 'KEP address',
      website: 'Website',
    },
  },
} as const

export default function KunyePage({ lang = 'tr' }: { lang?: LegalLang }) {
  const strings = L[lang]
  const { data } = useQuery({
    queryKey: ['company-info'],
    queryFn: getCompanyInfo,
    staleTime: 5 * 60 * 1000,
  })
  const c = data?.company

  const address = [c?.['company.address'], c?.['company.city']].filter(Boolean).join(', ')
  const rows: Array<[string, string]> = c
    ? ([
        [strings.labels.legalName, c['company.legal_name']],
        [strings.labels.tradeName, c['company.trade_name']],
        [strings.labels.taxOffice, c['company.tax_office']],
        [strings.labels.taxNo, c['company.tax_no']],
        [strings.labels.mersisNo, c['company.mersis_no']],
        [strings.labels.address, address],
        [strings.labels.phone, c['company.phone']],
        [strings.labels.email, c['company.email']],
        [strings.labels.kep, c['company.kep']],
        [strings.labels.website, c['company.website']],
      ] as Array<[string, string]>).filter(([, v]) => v.trim() !== '')
    : []

  const legalEmail = c?.['company.legal_email'] || c?.['company.email'] || ''
  const hostingNotice = c?.['company.hosting_notice'] || ''

  return (
    <LegalShell lang={lang} page="kunye" title={strings.title} updated={strings.updated}>
      <p>{strings.intro}</p>

      {rows.length > 0 && (
        <>
          <h2>{strings.infoHeading}</h2>
          <ul>
            {rows.map(([label, value]) => (
              <li key={label}>
                <strong>{label}:</strong> {value}
              </li>
            ))}
          </ul>
        </>
      )}

      {legalEmail !== '' && (
        <>
          <h2>{strings.legalHeading}</h2>
          <p>
            {strings.legalIntro}
            <a href={`mailto:${legalEmail}`}>{legalEmail}</a>
          </p>
        </>
      )}

      <h2>{strings.hostingHeading}</h2>
      <p>
        {strings.hostingStatus}
        {hostingNotice !== '' && <> {hostingNotice}</>}
      </p>
      <p>{strings.hostingLiability}</p>
    </LegalShell>
  )
}
